import { FC, useMemo, useRef, useEffect, useState } from 'react'
import { CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js'
import styles from 'components/features/pfm/StripeForm/StripeForm.styl'
import SvgIcon from 'components/atoms/SvgIcon'
import LockSvg from 'icons/lock.svg'
import { StripeCardNumber } from 'components/features/pfm/StripeCardNumber/StripeCardNumber'
import Flex from 'components/atoms/Flex/Flex'
import { InputDecoratedWithStripe } from 'components/atoms/inputs/InputDecoratedWithStripe/InputDecoratedWithStripe'

const getPromise = (): [Promise<any>, () => void] => {
  let resolve = () => {}
  const promise: Promise<void> = new Promise((r) => {
    resolve = r
  })
  return [promise, resolve]
}

type StripeFormProps = {
  onEscape: () => void
  onReady: () => void
}

export const StripeForm: FC<StripeFormProps> = (props) => {
  const { onEscape, onReady } = props
  const [formIsReady, setFormIsReady] = useState(false)

  const cardNumberLabel = useMemo(() => (
    <div className={ styles.numberLabel }>
      Card Number
      <div className={ styles.numberIconWrapper }>
        <div className={ styles.numberIcon }>
          <SvgIcon src={ LockSvg } />
        </div>
      </div>
    </div>
  ), [])

  const mountRef = useRef(false)
  useEffect(() => {
    mountRef.current = true
    return () => {
      mountRef.current = false
    }
  }, [])

  const readyHandlers = useMemo(() => {
    const [cardNumberPromise, cardNumber] = getPromise()
    const [cardExpiryPromise, cardExpiry] = getPromise()
    const [cardCvcPromise, cardCvc] = getPromise()

    Promise.all([
      cardNumberPromise,
      cardExpiryPromise,
      cardCvcPromise,
    ]).then(() => {
      if (!mountRef.current) {
        return
      }
      setFormIsReady(true)
      onReady()
    })

    return {
      cardNumber,
      cardExpiry,
      cardCvc,
    }
  }, [onReady])

  return (
    <Flex component='form' className={ styles.stripeForm }>
      <InputDecoratedWithStripe
        label={ cardNumberLabel }
        className={ styles.cardNumber }
        component={ StripeCardNumber }
        isReady={ formIsReady }
        onEscape={ onEscape }
        onReady={ readyHandlers.cardNumber }
      />
      <Flex horizontal gap='large' className={ styles.bottomRow }>
        <InputDecoratedWithStripe
          label='Expiration Date'
          className={ styles.expirationDate }
          component={ CardExpiryElement }
          isReady={ formIsReady }
          onEscape={ onEscape }
          onReady={ readyHandlers.cardExpiry }
        />
        <InputDecoratedWithStripe
          label='Security Code'
          className={ styles.securityCode }
          component={ CardCvcElement }
          isReady={ formIsReady }
          onEscape={ onEscape }
          onReady={ readyHandlers.cardCvc }
        />
      </Flex>
    </Flex>
  )
}
